/**
 * Doorly Pricing Tool — /price-tool
 *
 * 🔑 STAFF SESSIONS ONLY, DECIDED BY THE API (2026-10-21) — no shared password. The calculator
 * body is a self-contained HTML/JS widget, served by GET /api/price-tool/widget and injected via innerHTML; its
 * <script> is re-created with document.createElement so the browser
 * actually executes it (innerHTML alone won't run script tags). The
 * widget's IIFE prevents global scope collisions.
 *
 * ⚠️ window.setOption IS GONE, WITH THE TWO OPTIONS IT SWITCHED BETWEEN. The
 * tool used to offer "5% / 2% fee" and "10% / no fee" buttons and expose a
 * global for their inline onclick handlers. There is one SmartEstimate now —
 * 10% resale, 3% fee — so the buttons, the global and the branch that read
 * `currentOption === 5` are all removed rather than left switching between a
 * live structure and a retired one.
 */
import React, { useEffect, useRef, useState } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// 🔑 THE CALCULATOR IS SERVED, NOT BUNDLED (Jesse, 2026-10-21): GET /api/price-tool/widget answers a DoorlyDesk staff
// session only (backend/assets/price-tool/widget.html). Nothing about Doorly's deal economics ships in the bundle.
function PriceToolBody({ html }) {
  const toolRef = useRef(null);

  useEffect(() => {
    const container = toolRef.current;
    if (!container || !html) return;
    container.innerHTML = html;
    // innerHTML doesn't execute inline <script> tags. Find each one,
    // clone it as a fresh element, and replace — the browser will then
    // run the cloned script. Strict-mode double-mount in dev re-runs
    // this effect, which is intentional: every mount re-installs the
    // IIFE and its event listeners against a fresh DOM.
    Array.from(container.querySelectorAll('script')).forEach((oldScript) => {
      const newScript = document.createElement('script');
      newScript.textContent = oldScript.textContent;
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
    return () => {
      // Cleanup: nuke the DOM the script was bound against so a
      // remount doesn't double-fire listeners or leave a handler pointing at a
      // detached node.
      container.innerHTML = '';
    };
  }, [html]);

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', padding: '24px' }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: '24px',
        }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: '18px' }}>Doorly Pricing Tool</div>
            <div style={{ fontSize: '13px', color: '#6b7280' }}>Deal economics calculator</div>
          </div>
          <a href="/" style={{ fontSize: '13px', color: '#6b7280', textDecoration: 'none' }}>
            ← Back to app
          </a>
        </div>
        <div ref={toolRef} />
      </div>
    </div>
  );
}

export default function PriceTool() {
  // Staff only, decided by the API: the DoorlyDesk session is sent; no session → sign in; anyone else → no access.
  const [state, setState] = useState({ status: null, html: null });
  useEffect(() => {
    let alive = true;
    let token = null;
    try { token = localStorage.getItem('doorly_admin_token'); } catch (_) { token = null; }
    if (!token) { setState({ status: 401, html: null }); return undefined; }
    fetch(`${API_URL}/api/price-tool/widget`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (r) => ({ status: r.status, body: r.ok ? await r.json() : null }))
      .then(({ status, body }) => { if (alive) setState({ status, html: body ? body.html : null }); })
      .catch(() => { if (alive) setState({ status: 0, html: null }); });
    return () => { alive = false; };
  }, []);
  if (state.status === 200) return <PriceToolBody html={state.html} />;
  return (
    <div data-testid="price-tool-locked" style={{ minHeight: '100vh', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, boxSizing: 'border-box' }}>
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16, padding: 28, maxWidth: 440, width: '100%', boxSizing: 'border-box' }}>
        <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8 }}>Doorly Pricing Tool</div>
        <div style={{ fontSize: 14, color: '#374151', lineHeight: 1.6 }}>
          {state.status == null ? 'Checking your access…'
            : state.status === 403 ? 'The pricing tool is available to Doorly staff only.'
              : <>Doorly staff only. <a href="/admin/login" style={{ color: '#3a6ad9', fontWeight: 600, textDecoration: 'underline' }}>Sign in to DoorlyDesk</a> to use it.</>}
        </div>
      </div>
    </div>
  );
}
